import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios.tsx";
import Navbar from "../../components/Navbar/index.tsx";
import Footer from "../../components/Footer/index.tsx";
import Input from "../../components/ui/input.tsx";
import Button from "../../components/ui/button.tsx";

const RESEND_TIMEOUT_SECONDS = 60;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const userRaw =
    typeof window !== "undefined" ? localStorage.getItem("user") : null;
  const user = userRaw ? JSON.parse(userRaw) : null;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");

  useEffect(() => {
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }
    if (user?.is_email_verified === true) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      // Primary expected endpoint
      let res = await api.post(`/auth/verify_email`, {
        email: user?.email,
        code: code,
      });
      // Fallback variations if primary not available

      const data = res.data || {};
      // If backend returns whole user object with token, prefer it
      if (data?.access_token) {
        localStorage.setItem("user", JSON.stringify(data));
      } else {
        // Otherwise patch existing user in localStorage
        const merged = {
          ...(user || {}),
          is_email_verified: data?.is_email_verified ?? true,
        };
        localStorage.setItem("user", JSON.stringify(merged));
      }
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || "Verification failed";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    if (resendCooldown > 0 || resendLoading) {
      return;
    }
    if (!user?.email) {
      setResendError("No email found. Please log in again.");
      return;
    }

    setResendLoading(true);
    setResendError("");
    setResendMessage("");
    try {
      await api.post(`/auth/resend_verification_code`, {
        email: user.email,
      });
      setResendMessage("A new verification code has been sent to your email.");
      setResendCooldown(RESEND_TIMEOUT_SECONDS);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to resend code";
      setResendError(String(msg));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="page">
      <Navbar />
      <div className="pt-24 w-full flex justify-center items-center min-h-[80vh]">
        <div className="container-site p-8 card-glass max-w-md ">
          <h1 className="heading-2 text-center">Verify your email</h1>
          <p className="subheading text-center text-white/80">
            We sent a verification code to {user?.email || "your email"}.
          </p>
          <form className="mt-6" onSubmit={onSubmit}>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="mb-3"
            />
            {error && <div className="text-rose-400 text-sm mb-2">{error}</div>}
            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? "Verifying..." : "Verify"}
            </Button>
            <div className="mt-4 flex justify-center items-center flex-col">
              <p className="text-sm text-white/70">Didn't receive the code?</p>
              <Button
                type="button"
                variant="link"
                className="px-0 text-indigo-300 hover:text-indigo-200"
                disabled={resendLoading || resendCooldown > 0}
                onClick={onResend}
              >
                {resendLoading
                  ? "Resending..."
                  : resendCooldown > 0
                  ? `Resend available in ${resendCooldown}s`
                  : "Resend code"}
              </Button>
              {resendMessage && (
                <div className="text-emerald-400 text-sm mt-2">
                  {resendMessage}
                </div>
              )}
              {resendError && (
                <div className="text-rose-400 text-sm mt-2">{resendError}</div>
              )}
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default VerifyEmail;
